import { Mic, RotateCcw, Square } from 'lucide-react';
import { Button } from '@artstorefronts/ui';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dictationMessageKey } from '@arthelper/shared/dictation';
import { useAudioTranscription } from '../hooks/useAudioTranscription';

interface CompactAudioRecorderProps {
  setValue: (value: string) => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

function AudioBar({ height }: { height: number }) {
  return <div className="w-[3px] bg-gray-500 transition-all duration-150" style={{ height: `${height}px` }} />;
}

function useAudioVisualization(isRecording: boolean) {
  const [barHeights, setBarHeights] = useState<number[]>(Array(20).fill(2));
  const animationFrameRef = useRef<number | undefined>(undefined);
  const frameCountRef = useRef(0);
  const levels = [2, 8, 16]; // More contrast between heights

  useEffect(() => {
    if (!isRecording) {
      setBarHeights(Array(20).fill(2));
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      return;
    }

    function updateBars() {
      frameCountRef.current += 1;

      // Only update every 6 frames to slow down the animation more
      if (frameCountRef.current % 6 === 0) {
        setBarHeights((prev) => {
          const newHeights = [...prev];
          for (let i = 0; i < newHeights.length - 1; i++) {
            newHeights[i] = newHeights[i + 1];
          }
          newHeights[newHeights.length - 1] = levels[Math.floor(Math.random() * levels.length)];
          return newHeights;
        });
      }

      animationFrameRef.current = requestAnimationFrame(updateBars);
    }

    animationFrameRef.current = requestAnimationFrame(updateBars);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isRecording]);

  return barHeights;
}

function CompactAudioRecorder({ setValue, className, size = 'sm' }: CompactAudioRecorderProps) {
  const { t } = useTranslation('chat');
  const { t: tCommon } = useTranslation('common');
  const { recorder, isTranscribing, error, isBlocked, canRetry, retry, handleStopRecording } = useAudioTranscription({
    onTranscriptionComplete: setValue,
  });

  const barHeights = useAudioVisualization(recorder.isRecording);
  // The startup window counts as recording for display: a tap there cancels, so the
  // button must not still read "start".
  const isCapturing = recorder.isRecording || recorder.isStarting;

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2">
        <div
          className={clsx(
            'flex items-center px-2 overflow-hidden transition-colors duration-200',
            size === 'sm' && 'h-8',
            size === 'md' && 'h-10',
            size === 'lg' && 'h-12',
            isCapturing && 'bg-gray-300 rounded-full px-4',
            className
          )}
        >
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={clsx(
              'p-2',
              size === 'sm' && 'h-6 w-6',
              size === 'md' && 'h-8 w-8',
              size === 'lg' && 'h-10 w-10'
            )}
            onClick={recorder.isRecording ? handleStopRecording : recorder.startRecording}
            disabled={isTranscribing || isBlocked}
            aria-label={isCapturing ? t('input.stop-dictation') : t('input.start-dictation')}
          >
            {isCapturing ? (
              <Square
                className={clsx(
                  `text-red-500 fill-current`,
                  size === 'sm' && 'size-4',
                  size === 'md' && 'size-5',
                  size === 'lg' && 'size-5'
                )}
              />
            ) : isTranscribing ? (
              <span
                className={clsx(
                  `animate-spin rounded-full border-2 border-gray-300 border-t-black`,
                  size === 'sm' && 'size-4',
                  size === 'md' && 'size-5',
                  size === 'lg' && 'size-5'
                )}
              />
            ) : (
              <Mic className={clsx(size === 'sm' && 'size-4', size === 'md' && 'size-5', size === 'lg' && 'size-5')} />
            )}
          </Button>
          {isCapturing && (
            <div className="w-24 h-6 flex items-center justify-center gap-[2px]">
              {barHeights.map((height, i) => (
                <AudioBar key={i} height={height} />
              ))}
            </div>
          )}
        </div>
        {canRetry && (
          <Button type="button" variant="ghost" size="sm" className="px-3 h-8 text-gray-700" onClick={retry}>
            <RotateCcw className="size-4" />
            <span>{tCommon('try-again')}</span>
          </Button>
        )}
      </div>
      {error && (
        <p role="status" aria-live="polite" className="text-red-500 text-sm mt-1 max-w-64">
          {t(dictationMessageKey(error))}
        </p>
      )}
    </div>
  );
}

export default CompactAudioRecorder;
